import {
  authAtom,
  COMPANY_NAME,
  getPrimaryColor,
  Icon,
  isMobileAtom,
  LOCAL_STORAGE_KEY,
  LoginModal,
  PAGE_NAME,
  SESSION_STORAGE_KEY,
  SideMenuBtn,
  Styles,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import GalaxyLogo from '@/assets/img/galaxy_logo.png';
import ColorLogo from '@/assets/img/jejuro_color_logo.png';
import { app, ORIGIN_URL } from '@/constants/common';
import { reset, trackGuestCheckoutClicked } from '@/utils/analytics';
import { mobonTrackLogout } from '@/utils/mobon';
import Drawer from './Drawer';
import * as S from './styles';

const LOGO_WIDTH = 150;
const LOGO_WIDTH_MOBILE = 173;

export type NavItem = {
  title: string;
  /** 단일 링크일 때 이동 경로 */
  moveTo?: string;
  /** 지정 시 진한 브랜드 컬러 등으로 메뉴를 강조한다. */
  color?: string;
  /** 드롭다운(이용안내)일 때 하위 항목 */
  children?: { title: string; moveTo: string }[];
};

/** 이용안내 하위 6개 (라우트: /guide/:topic) — 순서는 레퍼런스와 동일 */
export const GUIDE_ITEMS: { title: string; moveTo: string }[] = [
  { title: '대여자격', moveTo: '/guide/qualification' },
  { title: '셔틀버스 운행', moveTo: '/guide/shuttle' },
  { title: '수하물 규정', moveTo: '/guide/luggage' },
  { title: '차량인수/반납', moveTo: '/guide/pickup-return' },
  { title: '취소규정', moveTo: '/guide/cancellation' },
];

export const NAV_LINK_LIST: NavItem[] = [
  { title: '예약', moveTo: PAGE_NAME.search },
  { title: '이용안내', children: GUIDE_ITEMS },
  { title: '보험안내', moveTo: '/guide/insurance' },
  { title: '자주묻는질문', moveTo: PAGE_NAME.cs_center },
  { title: '이벤트', moveTo: PAGE_NAME.event },
];

const CommonHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const {
    recoilState: [user, setUser],
  } = useRecoil(authAtom);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const isHomePage = pathname === PAGE_NAME.index;
  const isSearchPage = pathname === PAGE_NAME.search;

  const handleLogin = () => {
    setIsLoginModalOpen(true);
  };

  const handleLogout = () => {
    // Mixpanel 사용자 식별 초기화
    reset();
    // 모비온(원태그) 로그아웃 발화
    mobonTrackLogout();

    localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    sessionStorage.removeItem(SESSION_STORAGE_KEY.PRE_RESERVATION_ID);
    setUser(null);
    navigate(PAGE_NAME.index);
  };

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [pathname]);

  return (
    <>
      {isDrawerOpen && isMobile && (
        <Drawer
          onClose={() => setIsDrawerOpen(false)}
          isDrawerOpen={isDrawerOpen}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
      )}
      <S.OuterWrapper
        isHomePage={isHomePage}
        isMobile={isMobile}
        hasShadow={(!isSearchPage || !isMobile) && !isHomePage}
      >
        {isMobile && (
          <S.IconWrapper>
            <Icon
              src={SideMenuBtn}
              width={24}
              height={24}
              iconcolor={Styles.SVG_FILTER.GREY_01}
              onClick={() => setIsDrawerOpen(true)}
            />
          </S.IconWrapper>
        )}
        <S.InnerWrapper isMobile={isMobile}>
          <S.NavLink
            logo
            tabIndex={1}
            onClick={() => {
              navigate(PAGE_NAME.index);
            }}
          >
            <S.LogoStack>
              <img
                src={ColorLogo}
                alt={`${COMPANY_NAME.JEJURO} 로고`}
                width={isMobile ? LOGO_WIDTH_MOBILE : LOGO_WIDTH}
              />
              <img
                src={GalaxyLogo}
                alt='갤럭시렌트카 로고'
                width={isMobile ? 96 : 90}
              />
            </S.LogoStack>
          </S.NavLink>
          {!isMobile && (
            <S.Gnb>
              <S.GnbPart>
                {NAV_LINK_LIST.map((nav) =>
                  nav.children ? (
                    <DropdownWrap key={nav.title}>
                      <S.NavLink tabIndex={1} color={nav.color}>
                        {nav.title}
                      </S.NavLink>
                      <DropdownMenu>
                        {nav.children.map((child) => (
                          <DropdownItem
                            key={child.title}
                            onClick={() => navigate(child.moveTo)}
                          >
                            {child.title}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </DropdownWrap>
                  ) : (
                    <S.NavLink
                      key={nav.title}
                      tabIndex={1}
                      color={nav.color}
                      onClick={() => nav.moveTo && navigate(nav.moveTo)}
                    >
                      {nav.title}
                    </S.NavLink>
                  ),
                )}
              </S.GnbPart>
              <S.GnbPart>
                {!!user ? (
                  <S.NavLink
                    tabIndex={1}
                    color={getPrimaryColor(app)}
                    onClick={handleLogout}
                  >
                    로그아웃
                  </S.NavLink>
                ) : (
                  <S.NavLink
                    tabIndex={1}
                    color={getPrimaryColor(app)}
                    onClick={handleLogin}
                  >
                    로그인
                  </S.NavLink>
                )}
                {!!user ? (
                  <S.NavLink
                    tabIndex={1}
                    color={getPrimaryColor(app)}
                    onClick={() => navigate(PAGE_NAME.my_page)}
                  >
                    마이페이지
                  </S.NavLink>
                ) : (
                  <S.NavLink
                    tabIndex={1}
                    color={getPrimaryColor(app)}
                    onClick={() => {
                      // 예약 조회(이름+전화) 클릭 이벤트 트래킹
                      trackGuestCheckoutClicked();
                      navigate(PAGE_NAME.find_reservation);
                    }}
                  >
                    예약 조회
                  </S.NavLink>
                )}
              </S.GnbPart>
            </S.Gnb>
          )}
        </S.InnerWrapper>
      </S.OuterWrapper>
      <LoginModal
        platform={isMobile ? 'mobile' : 'desktop'}
        isOpen={isLoginModalOpen}
        originUrl={import.meta.env.VITE_ORIGIN_URL}
        onClose={() => {
          setIsLoginModalOpen(false);
        }}
      />
    </>
  );
};

export default CommonHeader;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translate(-50%, 10px);
  min-width: 168px;
  padding: 8px;

  /* 트리거와 메뉴 사이 빈 틈을 투명 브릿지로 메워, 마우스를 리스트로 옮기는
     동안 hover가 끊겨 메뉴가 닫히던 문제를 막는다(hover-gap). */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -14px;
    height: 14px;
  }
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: ${Styles.COLOR.WHITE};
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  z-index: 20;
`;

const DropdownWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &:hover ${DropdownMenu} {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 4px);
  }
`;

const DropdownItem = styled.button`
  text-align: left;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
  white-space: nowrap;
  ${Styles.FONT.B1};
  color: ${Styles.COLOR.GREY_01};

  &:hover {
    background-color: ${Styles.COLOR.GREY_BG};
    color: ${getPrimaryColor(app)};
  }
`;
